import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight, Instagram, Facebook, Phone, Mail, MapPin } from 'lucide-react';
import { studioData, navLinks } from '../data.js';
import { getOptimizedImageUrl } from '../lib/utils.js';

export const Button = ({ children, variant = 'primary', className = '', ...props }) => {
  const variants = {
    primary: 'bg-[#A8814E] text-white hover:bg-[#967345] border border-[#A8814E] shadow-sm',
    secondary: 'bg-[#CEBDAA] text-[#1C1B1A] hover:bg-[#C2B19E] border border-[#CEBDAA] shadow-sm',
    outline: 'bg-transparent border border-[#A8814E] text-[#A8814E] hover:bg-[#A8814E] hover:text-white',
    ghost: 'text-[#494540] hover:text-[#1C1B1A] hover:bg-[#F1E5DC]',
    soft: 'bg-[#F1E5DC] text-[#1C1B1A] hover:bg-[#E8D9CE] border border-[#CEBDAA]'
  };

  return (
    <motion.button
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={`px-6 py-3 sm:px-8 sm:py-4 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] transition-all duration-300 inline-flex items-center justify-center gap-2 cursor-pointer ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </motion.button>
  );
};

export const Badge = ({ children, className = '' }) => (
  <span className={`inline-flex items-center gap-3 rounded-full border border-[#CEBDAA]/50 bg-[#F1E5DC]/60 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#9A8E81] backdrop-blur-md ${className}`}>
    <span className="h-px w-5 bg-[#A8814E]/40" />
    {children}
  </span>
);

export const Heading = ({ children, level = 2, className = '', subtitle = '', badgeClassName = '' }) => {
  const Tag = `h${level}`;
  return (
    <div className={`mb-12 sm:mb-16 max-w-5xl ${className}`}>
      {subtitle && <Badge className={`mb-6 ${badgeClassName}`}>{subtitle}</Badge>}
      <Tag className="font-serif text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-normal tracking-[-0.05em] text-[#1C1B1A] leading-[0.95]">
        {children}
      </Tag>
    </div>
  );
};

export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  return (
    <nav className="fixed left-0 top-0 z-50 w-full px-3 py-3 sm:px-6">
      <div
        className={`mx-auto flex max-w-[1680px] items-center justify-between border transition-all duration-500 ${
          isScrolled
            ? 'py-2 pl-2 pr-4 sm:pl-3 sm:pr-8 border-[#CEBDAA]/80 bg-[#F1E5DC]/95 text-[#1C1B1A] shadow-lg shadow-[#090807]/5 backdrop-blur-xl'
            : 'py-3 pl-3 pr-6 sm:pl-4 sm:pr-10 border-[#CEBDAA]/40 bg-[#F1E5DC] text-[#1C1B1A] shadow-sm'
        }`}
      >
        <Link to="/" className="group flex shrink-0 items-center">
          <div className={`flex items-center justify-center transition-all duration-500 ${
            isScrolled ? 'h-12 w-auto max-w-[120px]' : 'h-16 w-auto max-w-[160px]'
          }`}>
            <img 
              src={getOptimizedImageUrl(studioData.logo, { width: 320, quality: 85 })} 
              alt={studioData.name} 
              className="h-full w-auto object-contain" 
            />
          </div>
        </Link>

        <div className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`relative text-[10px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 whitespace-nowrap ${
                location.pathname === link.path
                  ? 'text-[#A8814E]'
                  : 'text-[#9A8E81] hover:text-[#1C1B1A]'
              }`}
            >
              {link.name}
              <span className={`absolute -bottom-2 left-0 h-px bg-current transition-all duration-300 ${location.pathname === link.path ? 'w-full' : 'w-0'}`} />
            </Link>
          ))}
          <Button
            variant="primary"
            className="py-3 px-5 text-[10px]"
            onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}
          >
            Book nu
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>

        <button
          className="xl:hidden rounded-full border border-[#CEBDAA]/50 p-2 text-[#1C1B1A] hover:bg-[#CEBDAA]/20 transition-colors"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Åbn menu"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.28 }}
            className="fixed inset-0 z-[60] bg-[#090807] text-[#F1E5DC] xl:hidden"
          >
            <div className="flex h-full flex-col p-6 sm:p-10">
              <div className="mb-10 flex items-center justify-between">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center">
                  <div className="h-16 w-auto max-w-[140px] flex items-center justify-center">
                    <img src={studioData.logo} alt={studioData.name} className="h-full w-auto object-contain" />
                  </div>
                </Link>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-full border border-white/10 p-3 text-white hover:bg-white/10 transition-colors"
                  aria-label="Luk menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex flex-col gap-4 overflow-y-auto pb-10">
                {navLinks.map((link, i) => (
                  <motion.div
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                    key={link.path}
                  >
                    <Link
                      to={link.path}
                      className={`font-serif text-2xl sm:text-3xl tracking-tight transition-colors ${
                        location.pathname === link.path ? 'text-[#A8814E]' : 'text-[#9A8E81] hover:text-white'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </div>

              <div className="mt-auto border-t border-white/10 pt-8">
                <Button
                  variant="secondary"
                  className="w-full py-5"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.open('https://booking.lkstudioems.com/book-online', '_blank');
                  }}
                >
                  Book din tid
                </Button>
                <div className="mt-8 flex justify-center gap-6 text-stone-500">
                  <a href="https://www.instagram.com/lkstudio_cph/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" aria-label="Instagram">
                    <Instagram className="h-6 w-6" />
                  </a>
                  <a href="#" className="hover:text-white transition-colors" aria-label="Facebook">
                    <Facebook className="h-6 w-6" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export const Section = ({ children, className = '', id = '', light = false, noPadding = false }) => (
  <section
    id={id}
    className={`relative overflow-x-clip ${light ? 'bg-white' : 'bg-[#F1E5DC]'} ${className}`}
  >
    {!light && (
      <>
        <div className="pointer-events-none absolute left-0 top-0 h-px w-full bg-gradient-to-r from-transparent via-[#CEBDAA] to-transparent" />
        <div className="pointer-events-none absolute -left-40 top-10 h-80 w-80 rounded-full bg-[#CEBDAA]/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-[#A8814E]/10 blur-3xl" />
      </>
    )}

    <div className={`container relative z-10 mx-auto px-4 sm:px-6 ${noPadding ? '' : 'py-16 sm:py-20 lg:py-28'}`}>
      {children}
    </div>
  </section>
);

export const InstagramWidget = ({ widgetId = '5212f119-e738-4178-8432-0019620eb8e6' }) => {
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Add a tiny delay to ensure scrolling has finished and main thread is free
          const timer = setTimeout(() => {
            setIsInView(true);
          }, 150);
          observer.disconnect();
          return () => clearTimeout(timer);
        }
      },
      { rootMargin: '50px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isInView) return;

    const existingScript = document.querySelector('script[src="https://elfsightcdn.com/platform.js"]');
    if (existingScript) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    document.body.appendChild(script);
  }, [isInView]);

  return (
    <div ref={containerRef} className="w-full min-h-[450px] relative">
      {isInView ? (
        <div 
          className={`elfsight-app-${widgetId}`} 
          data-elfsight-app-lazy
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#EAE0D5]/20 rounded-2xl border border-[#1C1B1A]/5 p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#A8814E]/10 text-[#A8814E] mb-4 animate-pulse">
            <Instagram className="h-6 w-6" />
          </div>
          <h4 className="font-serif text-lg text-[#1C1B1A] mb-1">Følg os på Instagram</h4>
          <p className="text-xs text-[#1C1B1A]/60 max-w-xs mb-4">@lk_studio_fysioterapi_ems</p>
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#A8814E]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#A8814E] animate-ping" />
            Henter seneste opslag...
          </div>
        </div>
      )}
    </div>
  );
};

export const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-[#090807] px-4 py-16 text-[#9A8E81] sm:px-6 lg:py-24">
      <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#494540]/30 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="mb-14 grid grid-cols-1 gap-10 border-b border-white/10 pb-14 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Link to="/" className="mb-8 block">
              <div className="h-20 w-auto max-w-[160px] flex items-center justify-start">
                <img src={getOptimizedImageUrl(studioData.logo, { width: 320, quality: 85 })} alt={studioData.name} className="h-full w-auto object-contain" />
              </div>
            </Link>
            <p className="max-w-xs text-sm leading-7 text-[#9A8E81]">
              EMS træning, fysioterapi og kropslig styrke i et privat studie på Frederiksberg.
            </p>
            <div className="mt-8 flex gap-3">
              <a href="https://www.instagram.com/lkstudio_cph/" target="_blank" rel="noopener noreferrer" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-[#9A8E81] transition-all duration-300 hover:bg-[#A8814E] hover:text-white" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="#" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-[#9A8E81] transition-all duration-300 hover:bg-[#A8814E] hover:text-white" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="mb-6 text-xs font-semibold uppercase tracking-[0.24em] text-white">Navigation</h4>
            <ul className="space-y-3 text-sm">
              {navLinks.map((link) => (
                <li key={link.path}><Link to={link.path} className="hover:text-[#A8814E] transition-colors">{link.name}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-6 text-xs font-semibold uppercase tracking-[0.24em] text-white">Kontakt</h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a href={`mailto:${studioData.email}`} className="flex items-center gap-3 hover:text-[#A8814E] transition-colors">
                  <Mail className="h-4 w-4 text-[#A8814E]" />
                  {studioData.email}
                </a>
              </li>
              <li>
                <a href={`tel:${studioData.phone}`} className="flex items-center gap-3 hover:text-[#A8814E] transition-colors">
                  <Phone className="h-4 w-4 text-[#A8814E]" />
                  {studioData.phoneDisplay}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 text-[#A8814E]" />
                <span>{studioData.address}</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="mb-6 text-xs font-semibold uppercase tracking-[0.24em] text-white">Book</h4>
            <p className="mb-6 text-sm leading-7 text-[#9A8E81]">
              Start med en prøvetime og mærk, om EMS passer til din krop og hverdag.
            </p>
            <Button
              variant="secondary"
              className="w-full py-3"
              onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}
            >
              Book din tid
            </Button>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 text-xs text-[#494540] md:flex-row">
          <p>© {new Date().getFullYear()} {studioData.name}. Alle rettigheder forbeholdes.</p>
          <div className="flex gap-6">
            <span>CVR: {studioData.cvr}</span>
            <Link to="/kontakt" className="hover:text-white transition-colors">Privatlivspolitik</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
